import { Controller } from "@hotwired/stimulus"

// Admin issue-log table — column show/hide picker + drag-to-resize
// headers. State persists per-admin in localStorage so the next visit
// honors the layout. Two controller mounts on the page (one for the
// picker UI, one for the table) share the same localStorage so
// changes in the picker reflect on the table immediately.
const STORE_KEY = "pcpm.admin_issue_table.v1"

export default class extends Controller {
  static targets = ["picker"]

  connect() {
    this._state = this._load()
    this._apply()
    // Cross-mount sync: refresh when another controller instance
    // (the picker / the table) writes new state.
    this._onStorage = (e) => {
      if (e.key !== STORE_KEY) return
      this._state = this._load()
      this._apply()
    }
    window.addEventListener("storage", this._onStorage)
    // Outside-click closes the picker dropdown.
    this._onDocClick = (e) => {
      if (!this.hasPickerTarget) return
      if (this.pickerTarget.classList.contains("hidden")) return
      if (this.element.contains(e.target)) return
      this.pickerTarget.classList.add("hidden")
    }
    document.addEventListener("click", this._onDocClick, true)
  }

  disconnect() {
    window.removeEventListener("storage", this._onStorage)
    document.removeEventListener("click", this._onDocClick, true)
    if (this._dragHandlers) this._endResize()
  }

  togglePicker(event) {
    event?.preventDefault?.()
    if (!this.hasPickerTarget) return
    this.pickerTarget.classList.toggle("hidden")
    // Sync the checkboxes to current state when opening.
    if (!this.pickerTarget.classList.contains("hidden")) {
      this.pickerTarget.querySelectorAll("input[data-col-key]").forEach(cb => {
        cb.checked = !this._state.hidden.includes(cb.dataset.colKey)
      })
    }
  }

  toggleCol(event) {
    const key = event.currentTarget.dataset.colKey
    const idx = this._state.hidden.indexOf(key)
    if (event.currentTarget.checked) {
      if (idx >= 0) this._state.hidden.splice(idx, 1)
    } else {
      if (idx < 0) this._state.hidden.push(key)
    }
    this._save()
    this._apply()
  }

  resetCols(event) {
    event?.preventDefault?.()
    this._state = { hidden: [], widths: {} }
    this._save()
    this._apply()
    // Re-tick all the boxes in the picker.
    if (this.hasPickerTarget) {
      this.pickerTarget.querySelectorAll("input[data-col-key]").forEach(cb => cb.checked = true)
    }
  }

  // Drag-to-resize on a header's right edge. We move the matching
  // <col> element's width as the mouse moves, then commit the final
  // width to localStorage when the user releases.
  startResize(event) {
    event.preventDefault()
    event.stopPropagation()
    const key = event.currentTarget.dataset.colKey
    if (!key) return
    const colEl = this._findColEl(key)
    const th = event.currentTarget.parentElement
    if (!colEl || !th) return
    const startX = event.clientX
    const startW = th.getBoundingClientRect().width
    document.body.classList.add("issue-table-resizing")
    const onMove = (e) => {
      const next = Math.max(40, Math.round(startW + (e.clientX - startX)))
      colEl.style.width = `${next}px`
    }
    const onUp = () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup",   onUp)
      document.body.classList.remove("issue-table-resizing")
      this._dragHandlers = null
      // Commit the final width.
      const finalW = parseInt(colEl.style.width || "0", 10)
      if (finalW) {
        this._state.widths[key] = finalW
        this._save()
      }
    }
    this._dragHandlers = { onMove, onUp }
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup",   onUp)
  }

  _endResize() {
    if (!this._dragHandlers) return
    document.removeEventListener("mousemove", this._dragHandlers.onMove)
    document.removeEventListener("mouseup",   this._dragHandlers.onUp)
    document.body.classList.remove("issue-table-resizing")
    this._dragHandlers = null
  }

  _apply() {
    // Apply hidden state across all matching mounts on the page.
    document.querySelectorAll(".issue-table").forEach(table => {
      // Cell visibility — col, th, td all carry data-col-key.
      table.querySelectorAll("[data-col-key]").forEach(el => {
        const hide = this._state.hidden.includes(el.dataset.colKey)
        el.style.display = hide ? "none" : ""
      })
      // Per-column widths.
      table.querySelectorAll("col[data-col-key]").forEach(col => {
        const w = this._state.widths[col.dataset.colKey]
        if (w) col.style.width = `${w}px`
      })
    })
  }

  _findColEl(key) {
    return document.querySelector(`.issue-table col[data-col-key="${CSS.escape(key)}"]`)
  }

  _load() {
    try {
      const raw = localStorage.getItem(STORE_KEY)
      const v = raw ? JSON.parse(raw) : {}
      return {
        hidden: Array.isArray(v.hidden) ? v.hidden : [],
        widths: (v.widths && typeof v.widths === "object") ? v.widths : {}
      }
    } catch (_) {
      return { hidden: [], widths: {} }
    }
  }

  _save() {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(this._state)) } catch (_) {}
  }
}
